import { Alert, Stack, Text } from '@mantine/core';
import type { TabReport } from '@mdev/common';
import { NutritionVariantTable } from './NutritionVariantTable';

const TAB_DESCRIPTIONS: Record<string, string> = {
  'one-carbon':
    'Genes involved in folate, B12, and choline metabolism affecting methylation pathways',
  'vitamin-d': 'Variants affecting vitamin D synthesis, transport, and metabolism',
  'vitamin-c': 'Genetic variants affecting vitamin C cellular transport',
  iron: 'Variants affecting iron absorption, transport, and storage',
  pufas: 'Genes affecting omega-3 and omega-6 fatty acid metabolism',
  detox: 'Phase I, II, and III detoxification enzyme variants',
  oxidative: 'Antioxidant enzyme variants affecting oxidative stress response',
  'gi-tolerance': 'Lactose tolerance and celiac disease risk markers',
  metabolic: 'Variants affecting appetite regulation and metabolic traits',
};

export function TabContent({ tabReport, activeTab }: { tabReport: TabReport; activeTab: string }) {
  const isCeliacTab = activeTab === 'gi-tolerance';
  const allVariants = tabReport.geneGroups.flatMap((group) => group.variants);

  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        {TAB_DESCRIPTIONS[activeTab] ?? 'Genetic variants for this pathway'}
      </Text>

      {isCeliacTab && (
        <Alert color="yellow" title="Important Disclaimer" variant="light">
          <Text size="sm">
            Celiac HLA tags are <strong>risk markers only</strong>, not diagnostic tests. Presence
            of risk alleles does not mean you have celiac disease. Clinical testing with serology
            and/or biopsy is required for diagnosis. Consult a healthcare provider for proper
            evaluation.
          </Text>
        </Alert>
      )}

      {activeTab === 'metabolic' && (
        <Alert color="blue" title="Context Only" variant="light">
          <Text size="sm">
            These variants provide context about metabolic traits and appetite regulation. They
            should not be used to determine supplement dosing or treatment plans. Always consider
            individual health status and consult healthcare providers.
          </Text>
        </Alert>
      )}

      {tabReport.geneGroups.map((geneGroup) => (
        <NutritionVariantTable
          key={geneGroup.gene}
          geneGroup={geneGroup}
          showCNVWarnings={activeTab === 'detox'}
          allVariants={allVariants}
        />
      ))}

      {tabReport.geneGroups.length === 0 && (
        <Alert color="gray" variant="light">
          <Text size="sm">No variants found for this pathway in your genetic data.</Text>
        </Alert>
      )}
    </Stack>
  );
}
